import React, { useEffect, useRef, useState } from "react";
import SelectableCountButton from "./SelectableCountButton";
import { ACCESS_TYPE, AccessGrant, AdminUser } from "../../models/AdminUser";

/**
 * Active tab → formData key mapping
 */
const TAB_FIELD_MAP: Record<string, keyof AdminUser> = {
  Coordinator: "coordinators",
  Developer: "tmsPersons",
  Teacher: "teachers",
  Support: "supportPersons",
  Media: "mediaPersons",
};

const InstructorUserSelector: React.FC<{
  users: AdminUser[];
  formData: AdminUser;
  setFormData: React.Dispatch<React.SetStateAction<AdminUser>>;
  currentActiveTab: string | null;
}> = ({ users, formData, setFormData, currentActiveTab }) => {
  const [popoverUserId, setPopoverUserId] = useState<string | null>(null);
  const [accessType, setAccessType] = useState<ACCESS_TYPE>(ACCESS_TYPE.VIEW);
  const popoverRef = useRef<HTMLDivElement | null>(null);

  /**
   * Get active AccessGrant array
   */
  const getActiveArray = (): AccessGrant[] => {
    if (!currentActiveTab) return [];
    const key = TAB_FIELD_MAP[currentActiveTab];
    return (formData[key] as AccessGrant[]) || [];
  };

  /**
   * Check if user exists in array
   */
  const isChecked = (userId: string) =>
    getActiveArray().some((u) => u.id === userId);

  /**
   * Add / remove user using checkbox
   */
  const toggleUser = (user: AdminUser, checked: boolean) => {
    if (!currentActiveTab) return;
    const key = TAB_FIELD_MAP[currentActiveTab];
    const userId = (user as any)._id;

    setFormData((prev) => {
      const existing = (prev[key] as AccessGrant[]) || [];

      if (!checked) {
        return {
          ...prev,
          [key]: existing.filter((u) => u.id !== userId),
        };
      }

      return {
        ...prev,
        [key]: [
          ...existing,
          {
            id: userId,
            name: user.name,
            accessType: ACCESS_TYPE.VIEW, // default
          },
        ],
      };
    });
  };

  /**
   * Open mini popover
   */
  const openPopover = (user: AdminUser) => {
    const uid = (user as any)._id;
    const existing = getActiveArray().find((u) => u.id === uid);

    setAccessType(existing?.accessType || ACCESS_TYPE.VIEW);
    setPopoverUserId(uid);
  };

  const getUserAccessType = (user: AdminUser) => {
    const uid = (user as any)._id;
    const existing = getActiveArray().find((u) => u.id === uid);
    return (existing?.accessType || ACCESS_TYPE.VIEW)
  }

  /**
   * Update accessType immediately
   */
  const updateAccessType = (type: ACCESS_TYPE) => {
    if (!currentActiveTab || !popoverUserId) return;
    const key = TAB_FIELD_MAP[currentActiveTab];

    setAccessType(type);

    setFormData((prev) => {
      const existing = (prev[key] as AccessGrant[]) || [];

      return {
        ...prev,
        [key]: existing.map((u) =>
          u.id === popoverUserId ? { ...u, accessType: type } : u
        ),
      };
    });
  };

  /**
   * Close popover
   */
  const closePopover = () => setPopoverUserId(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node)
      ) {
        closePopover();
      }
    };

    if (popoverUserId) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [popoverUserId]);


  return (
    <div className="flex flex-wrap gap-2">
      {users.map((user) => {
        const uid = (user as any)._id;

        return (
          <div key={uid} className="relative">

            <SelectableCountButton
              label={user.name}
              checked={isChecked(uid)}
              onChange={(val) => toggleUser(user, val)}
              onButtonClick={() => openPopover(user)}
              isHasFullPermission={getUserAccessType(user as any) === ACCESS_TYPE.FULL}
            />

            {popoverUserId === uid && (
              <div ref={popoverRef} className="absolute top-full mt-2 right-[-2rem] z-[9999] w-40 bg-white border rounded-lg shadow-xl p-3">
                <div className="space-y-2">
                  <h3 className="font-bold text-sm">
                    Access Grant
                  </h3>
                  <label className="flex items-center cursor-pointer gap-2 text-sm">
                    <input
                      type="radio"
                      checked={accessType === ACCESS_TYPE.VIEW}
                      className="accent-greenBg"
                      onChange={() => updateAccessType(ACCESS_TYPE.VIEW)}
                    />
                    View
                  </label>

                  <label className="flex items-center cursor-pointer gap-2 text-sm">
                    <input
                      type="radio"
                      className="accent-greenBg"
                      checked={accessType === ACCESS_TYPE.FULL}
                      onChange={() => updateAccessType(ACCESS_TYPE.FULL)}
                    />
                    Full
                  </label>
                </div>

                <button
                  className="mt-3 w-full bg-greenBg text-white text-sm py-1.5 rounded-md"
                  onClick={closePopover}
                >
                  Update
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default InstructorUserSelector;
